import type {
  CreateProcessRequest,
  HabitaProcessDetail,
  HabitaProcessListItem,
  ProcessFileModel,
  ProcessFileStoragePath,
  ProcessModel,
  UpdateProcessRequest
} from '../../shared/models';
import {
  getProcessSortQueryParams,
  type ProcessSortField,
  type ProcessSortOrder,
} from '../../shared/utils/processOrdering';
import {
  getSdrResponsibleEmail,
  isSdrScopedUser,
} from '../../shared/utils/authStorage';
import { HttpRepository } from './HttpRepository';
import {
  getHabitaFileDownloadUrl,
  getHabitaProcessDetailUrl,
  getHabitaProcessFilesUrl,
  getHabitaProcessListUrl,
} from './habitaApiUrls';

export interface ProcessListPageParams {
  page: number;
  page_size: number;
  status?: string;
  searchText?: string;
  processoText?: string;
  createdBy?: string;
  responsibleEmail?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: ProcessSortField;
  sortOrder?: ProcessSortOrder;
}

export interface ProcessListPageResult {
  items: HabitaProcessListItem[];
  total: number;
  hasExplicitTotal?: boolean;
}

type CustomerDossierField<T = unknown> = {
  value?: T;
  source?: string | null;
  confidence?: number | null;
  created_at?: string | null;
  created_by?: string | null;
  source_detail?: {
    file_id?: number | null;
    file_type?: string | null;
    file_subtype?: string | null;
    source_name?: string | null;
    ocr_field?: string | null;
    [key: string]: unknown;
  } | null;
};

type CustomerDossier = Record<string, CustomerDossierField | null | undefined>;
type FileDownloadRequest = {
  authenticated: boolean;
  url: string;
};
type FileDownloadFailure = {
  error: unknown;
  url: string;
};

export interface ProcessFileDownloadResult {
  data: ArrayBuffer;
  mimeType?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseJsonLike(value: unknown): unknown {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed || !['{', '['].includes(trimmed[0])) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function recordFromJsonLike(value: unknown): Record<string, unknown> | null {
  const parsedValue = parseJsonLike(value);
  if (Array.isArray(parsedValue) && parsedValue.length === 1) {
    return recordFromJsonLike(parsedValue[0]);
  }
  return isRecord(parsedValue) ? parsedValue : null;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function firstPresent<T>(...values: T[]): T | undefined {
  return values.find((value) => value !== null && value !== undefined && String(value).trim() !== '');
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function firstStringValue(...values: unknown[]): string | null {
  for (const value of values) {
    const text = asString(value);
    if (text) return text;
  }

  return null;
}

function readProcessFileExtractedData(rawRecord: Record<string, unknown>): Record<string, unknown> | null {
  return recordFromJsonLike(rawRecord.extracted_data);
}

function arrayBufferToText(data: ArrayBuffer): string {
  return new TextDecoder().decode(data);
}

function firstBytes(data: ArrayBuffer): Uint8Array {
  return new Uint8Array(data.slice(0, 16));
}

function looksLikeJsonResponse(data: ArrayBuffer, contentType?: string | null): boolean {
  const normalizedContentType = contentType?.toLowerCase() ?? '';
  if (normalizedContentType.includes('application/json')) return true;

  const [firstByte] = firstBytes(data);
  return firstByte === 0x7b || firstByte === 0x5b;
}

function parseJsonResponse(data: ArrayBuffer): unknown {
  const text = arrayBufferToText(data).trim();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function looksLikeBase64(value: string): boolean {
  const cleanValue = value.replace(/\s/g, '');
  return cleanValue.length > 32 && cleanValue.length % 4 === 0 && /^[A-Za-z0-9+/]+={0,2}$/.test(cleanValue);
}

function base64ToArrayBuffer(value: string): ArrayBuffer {
  const binary = atob(value.replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message.trim() !== ''
    ? error.message
    : 'erro desconhecido';
}

function friendlyRepositoryError(message: string, cause: unknown): Error {
  const error = new Error(message);
  (error as Error & { cause?: unknown }).cause = cause;
  return error;
}

function isNotFoundError(error: unknown): boolean {
  return error instanceof Error && /HTTP Error:\s*404\b/.test(error.message);
}

function buildDownloadError(failures: FileDownloadFailure[]): Error {
  const emptyResponseFailure = failures.find((failure) => (
    errorMessage(failure.error).includes('arquivo vazio')
  ));

  if (emptyResponseFailure) {
    return friendlyRepositoryError('Falha ao encontrar o arquivo.', emptyResponseFailure.error);
  }

  const jsonWithoutFileFailure = failures.find((failure) => (
    errorMessage(failure.error).includes('JSON sem arquivo')
  ));

  if (jsonWithoutFileFailure) {
    return friendlyRepositoryError('Falha ao encontrar o arquivo.', jsonWithoutFileFailure.error);
  }

  const notFoundFailure = failures.find((failure) => isNotFoundError(failure.error));
  if (notFoundFailure) {
    return friendlyRepositoryError('Falha ao encontrar o arquivo.', notFoundFailure.error);
  }

  const lastFailure = failures[failures.length - 1];
  if (lastFailure) {
    return friendlyRepositoryError('Não foi possível baixar o arquivo do processo.', lastFailure.error);
  }

  return new Error('Não foi possível baixar o arquivo do processo.');
}

function findDownloadPayload(value: unknown, depth = 0): {
  base64?: string;
  mimeType?: string;
} | null {
  if (depth > 6 || value === null || value === undefined) return null;

  if (typeof value === 'string') {
    const cleanValue = value.trim();
    if (looksLikeBase64(cleanValue)) return { base64: cleanValue };
    return null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findDownloadPayload(item, depth + 1);
      if (found) return found;
    }
    return null;
  }

  if (!isRecord(value)) return null;

  const mimeType = asString(firstPresent(
    value.mimeType,
    value.mime_type,
    value.contentType,
    value.content_type,
    value.type
  ));

  const base64 = asString(firstPresent(
    value.base64,
    value.file_base64,
    value.fileBase64,
    value.content_base64,
    value.contentBase64
  ));
  if (base64 && looksLikeBase64(base64)) return { base64, mimeType: mimeType ?? undefined };

  const nestedValues = [
    value.data,
    value.body,
    value.file,
    value.document,
    value.payload,
    value.result,
    value.response,
  ];
  for (const nestedValue of nestedValues) {
    const found = findDownloadPayload(nestedValue, depth + 1);
    if (found) {
      return {
        mimeType: found.mimeType ?? mimeType ?? undefined,
        base64: found.base64,
      };
    }
  }

  return null;
}

function dossierValue<T = unknown>(dossier: CustomerDossier | null | undefined, key: string): T | undefined {
  const field = dossier?.[key];
  if (!field || typeof field !== 'object') return undefined;
  return field.value as T | undefined;
}

function normalizeAiStatus(rawAiStatus: unknown, rawFileStatus: unknown): ProcessFileModel['ai_status'] {
  const aiStatus = typeof rawAiStatus === 'string' ? rawAiStatus.trim() : '';
  if (aiStatus) return aiStatus;

  const fileStatus = typeof rawFileStatus === 'string' ? rawFileStatus.trim().toUpperCase() : '';
  if (fileStatus === 'AI_PROCESSING') return 'processing';
  if (fileStatus === 'EXTRACTION_OK') return 'completed';
  if (fileStatus === 'EXTRACTION_ERROR') return 'failed';
  return 'pending';
}

function normalizeProcessFile(raw: unknown, processId?: number | string | null): ProcessFileModel | null {
  const rawRecord = recordFromJsonLike(raw);
  if (!rawRecord) return null;

  const id = Number(rawRecord.id);
  if (!Number.isFinite(id)) return null;

  const extractedData = readProcessFileExtractedData(rawRecord);
  const rawProcessId = firstPresent(rawRecord.process_id, processId);
  const numericProcessId = rawProcessId === undefined ? null : Number(rawProcessId);
  const validationData = recordFromJsonLike(rawRecord.validation_data);
  const ruleCheckData = recordFromJsonLike(rawRecord.rule_check_data);
  const validationDataSource = validationData && Object.keys(validationData).length > 0
    ? validationData
    : ruleCheckData;
  const aiDiagnosis = recordFromJsonLike(rawRecord.ai_diagnosis);

  return {
    ...(rawRecord as Partial<ProcessFileModel>),
    id,
    name: typeof rawRecord.name === 'string' ? rawRecord.name : null,
    path: asString(rawRecord.path) ?? '',
    size: typeof rawRecord.size === 'number' ? rawRecord.size : null,
    status: typeof rawRecord.status === 'string' ? rawRecord.status : null,
    created_at: typeof rawRecord.created_at === 'string' ? rawRecord.created_at : null,
    ai_status: normalizeAiStatus(rawRecord.ai_status, rawRecord.status),
    extracted_data: extractedData as ProcessFileModel['extracted_data'] ?? null,
    confirmed_data: parseJsonLike(rawRecord.confirmed_data) ?? null,
    agentic_extractor_id: typeof rawRecord.agentic_extractor_id === 'string' ? rawRecord.agentic_extractor_id : null,
    ai_start: typeof rawRecord.ai_start === 'string' ? rawRecord.ai_start : null,
    ai_finish: typeof rawRecord.ai_finish === 'string' ? rawRecord.ai_finish : null,
    process_id: Number.isFinite(numericProcessId) ? numericProcessId : null,
    doc_type: firstStringValue(rawRecord.doc_type, rawRecord.docType) ?? '',
    doc_subtype: firstStringValue(rawRecord.doc_subtype, rawRecord.docSubtype),
    doc_subtype_text: firstStringValue(rawRecord.doc_subtype_text, rawRecord.docSubtypeText),
    validation_data: validationDataSource as ProcessFileModel['validation_data'] ?? null,
    rule_check_data: ruleCheckData as ProcessFileModel['rule_check_data'] ?? null,
    ai_diagnosis: aiDiagnosis as ProcessFileModel['ai_diagnosis'] ?? null,
  };
}

function normalizeProcessFiles(raw: unknown, processId?: number | string | null): ProcessFileModel[] {
  const parsed = parseJsonLike(raw);

  if (Array.isArray(parsed)) {
    return parsed
      .map((file) => normalizeProcessFile(file, processId))
      .filter((file): file is ProcessFileModel => file !== null);
  }

  throw new Error(
    `Contrato inválido dos arquivos do processo: a resposta deve ser um array (${describeProcessListPayload(raw)}).`
  );
}

function buildCustomerDataFromDossier(dossier: CustomerDossier | null | undefined): Record<string, unknown> {
  const residenceData = dossierValue<Record<string, unknown>>(dossier, 'residence_data') ?? {};
  const incomeData = dossierValue<Record<string, unknown>>(dossier, 'income_data') ?? {};
  const firstIncome = Array.isArray(incomeData.incomes) && isRecord(incomeData.incomes[0])
    ? incomeData.incomes[0]
    : {};
  const identificationData = dossierValue<Record<string, unknown>>(dossier, 'identification_data') ?? {};

  return {
    name: dossierValue(dossier, 'full_name'),
    full_name: dossierValue(dossier, 'full_name'),
    cpf: firstPresent(identificationData.cpf, identificationData.cpf_number),
    document_number: firstPresent(
      identificationData.cpf,
      identificationData.cpf_number,
      identificationData.document_number,
      identificationData.number
    ),
    birth_date: dossierValue(dossier, 'birth_date'),
    birth_place: dossierValue(dossier, 'birth_place'),
    nationality: dossierValue(dossier, 'nationality'),
    gender: dossierValue(dossier, 'gender'),
    parents: dossierValue(dossier, 'parents'),
    zipcode: residenceData.zipcode,
    cep: residenceData.zipcode,
    address: firstPresent(residenceData.full_address, residenceData.address_line_1),
    full_address: residenceData.full_address,
    city: residenceData.city,
    state: residenceData.state,
    gross_income: firstPresent(incomeData.avg_monthly_income, firstIncome.gross_income),
    net_income: firstIncome.net_income,
    avg_monthly_income: firstPresent(incomeData.avg_monthly_income, firstIncome.avg_monthly_income),
    main_occupation: firstPresent(incomeData.main_occupation, firstIncome.occupation),
    occupation: firstIncome.occupation,
  };
}

function normalizeProcessDetail(raw: unknown, processIdFallback?: number | string | null): any {
  const processDetail = parseJsonLike(raw);

  if (!isRecord(processDetail)) return processDetail;

  const processId = firstPresent(processDetail.id, processDetail.process_id, processIdFallback) as number | string | undefined;
  const customerDossier = recordFromJsonLike(processDetail.customer_dossier) as CustomerDossier | null;

  return {
    ...processDetail,
    id: firstPresent(processDetail.id, processId),
    process_id: firstPresent(processDetail.process_id, processId),
    customer_dossier: customerDossier ?? processDetail.customer_dossier,
    customer_data: buildCustomerDataFromDossier(customerDossier),
    files: null,
  };
}

function normalizeProcessListItem(raw: unknown): any {
  if (!isRecord(raw)) return raw;
  return raw;
}

function normalizeProcessListItems(items: unknown[]): any[] | null {
  if (items.length === 0) return [];
  if (!items.every((item) => isRecord(item) && item.id !== undefined)) return null;
  return items.map(normalizeProcessListItem);
}

function describeProcessListPayload(rawValue: unknown): string {
  const raw = parseJsonLike(rawValue);

  if (Array.isArray(raw)) return `array(${raw.length})`;
  if (isRecord(raw)) {
    const keys = Object.keys(raw).slice(0, 12).join(', ');
    return keys ? `chaves recebidas: ${keys}` : 'objeto sem chaves';
  }
  if (raw === null) return 'null';
  return typeof raw;
}

function parseProcessListResponse(rawValue: unknown): ProcessListPageResult {
  const raw = parseJsonLike(rawValue);
  if (isRecord(raw) && Array.isArray(raw.items)) {
    const items = normalizeProcessListItems(raw.items);
    if (items) {
      const explicitTotal = toFiniteNumber(raw.total);
      return {
        items,
        total: explicitTotal ?? raw.items.length,
        hasExplicitTotal: explicitTotal !== null,
      };
    }
  }

  throw new Error(
    `Contrato inválido da listagem de processos: a resposta não contém { items, total } (${describeProcessListPayload(rawValue)}).`
  );
}

export class ProcessRepository {
  private apiUrl = import.meta.env.VITE_API_URL;
  private httpRepository = new HttpRepository(this.apiUrl);
  private processApiUrl = import.meta.env.VITE_API_URL_ALT || import.meta.env.VITE_API_URL || '';
  private processHttpRepository = new HttpRepository(this.processApiUrl);
  private directHttpRepository = new HttpRepository();

  private buildFileDownloadRequests(
    file: ProcessFileStoragePath & { id?: number | string | null; process_id?: number | string | null }
  ): FileDownloadRequest[] {
    const requests: FileDownloadRequest[] = [];
    const seenUrls = new Set<string>();
    const pushRequest = (url: string, authenticated = true) => {
      if (seenUrls.has(url)) return;
      seenUrls.add(url);
      requests.push({ authenticated, url });
    };

    const numericFileId = Number(file.id);
    if (file.id !== null && file.id !== undefined && Number.isFinite(numericFileId) && numericFileId > 0) {
      pushRequest(getHabitaFileDownloadUrl(`/process/file/${numericFileId}`));
      return requests;
    }

    return requests;
  }

  private async downloadRequest(request: FileDownloadRequest): Promise<ProcessFileDownloadResult> {
    const response = await this.directHttpRepository.downloadFile(request.url, {
      authenticated: request.authenticated,
      headers: { Accept: '*/*' },
    });
    const { data } = response;
    const mimeType = response.headers.get('content-type') ?? undefined;

    if (data.byteLength === 0) {
      throw new Error('Endpoint de download retornou arquivo vazio.');
    }

    if (looksLikeJsonResponse(data, mimeType)) {
      const payload = findDownloadPayload(parseJsonResponse(data));
      if (!payload) {
        throw new Error('Endpoint de download retornou JSON sem arquivo ou base64.');
      }

      if (payload.base64) {
        return {
          data: base64ToArrayBuffer(payload.base64),
          mimeType: payload.mimeType,
        };
      }
    }

    return { data, mimeType };
  }

  /**
   * Insere um novo processo
   */
  async create(data: CreateProcessRequest): Promise<ProcessModel | null> {
    try {
      const response = await this.httpRepository.post<ProcessModel>('/processes', {
        ...data,
        created_at: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      console.error('Erro inesperado ao criar processo:', error);
      throw friendlyRepositoryError('Não foi possível criar o processo agora. Tente novamente em instantes.', error);
    }
  }

  /**
   * Atualiza um processo existente
   */
  async update(id: string, data: UpdateProcessRequest): Promise<ProcessModel | null> {
    try {
      const response = await this.httpRepository.put<ProcessModel>(`/processes/${id}`, {
        ...data,
        updated_at: new Date().toISOString(),
      });

      return response.data;
    } catch (error) {
      console.error('Erro inesperado ao atualizar processo:', error);
      throw friendlyRepositoryError('Não foi possível atualizar o processo agora. Tente novamente em instantes.', error);
    }
  }

  /**
   * Busca um processo por ID
   */
  async findById(id: string): Promise<ProcessModel | null> {
    try {
      const response = await this.directHttpRepository.get<unknown>(getHabitaProcessDetailUrl(id));
      return normalizeProcessDetail(response.data, id) as ProcessModel;
    } catch (error) {
      console.error('Erro inesperado ao buscar processo por ID:', error);
      if (isNotFoundError(error)) return null;
      throw friendlyRepositoryError('Não foi possível carregar os dados do processo agora. Tente novamente em instantes.', error);
    }
  }

  /**
   * Busca processos com informações do cliente (paginado).
   * Envia `page` e `page_size` na query string para a API.
   */
  async findAllWithCustomer(params: ProcessListPageParams): Promise<ProcessListPageResult> {
    try {
      const searchText = params.searchText?.trim();
      const processoText = params.processoText?.trim();
      const createdBy = params.createdBy?.trim();
      const shouldScopeByResponsible = isSdrScopedUser();
      const scopedResponsibleEmail = shouldScopeByResponsible ? getSdrResponsibleEmail()?.trim() : '';
      if (shouldScopeByResponsible && !scopedResponsibleEmail) {
        throw new Error('Não foi possível identificar o responsável do usuário logado.');
      }

      const responsibleEmail = shouldScopeByResponsible
        ? scopedResponsibleEmail
        : params.responsibleEmail?.trim();
      const querySearchText = searchText || processoText;
      const search = new URLSearchParams({
        page: String(params.page),
        page_size: String(params.page_size),
        ...getProcessSortQueryParams(params.sortOrder, params.sortBy),
        ...(params.status ? { status: params.status } : {}),
        ...(querySearchText ? { searchText: querySearchText } : {}),
        ...(processoText ? { processoText } : {}),
        ...(createdBy ? { createdBy } : {}),
        ...(responsibleEmail ? { responsible_email: responsibleEmail } : {}),
        ...(params.startDate ? { startDate: params.startDate } : {}),
        ...(params.endDate ? { endDate: params.endDate } : {}),
      });
      const response = await this.directHttpRepository.get<unknown>(`${getHabitaProcessListUrl()}?${search.toString()}`);
      return parseProcessListResponse(response.data);
    } catch (error) {
      console.error('Erro inesperado ao buscar processos com clientes:', error);
      throw friendlyRepositoryError('Não foi possível carregar os processos agora. Tente novamente em instantes.', error);
    }
  }

  /**
   * Faz upload de todos os documentos de uma vez
   */
  async uploadDocuments(
    documents: File,
    residenceProof: File,
    incomeProof: File,
    processName: string
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('documents', documents);
      formData.append('residence_proof', residenceProof);
      formData.append('income_proof', incomeProof);
      formData.append('process_name', processName);

      const response = await this.httpRepository.uploadFormData<any>('/process', formData);

      return response.data;
    } catch (error) {
      console.error('Erro inesperado ao fazer upload dos documentos:', error);
      throw error;
    }
  }

  /**
   * Busca arquivos de um processo específico
   */
  async getProcessFiles(processId: string): Promise<ProcessFileModel[]> {
    try {
      if (!this.processApiUrl) {
        throw new Error('URL da API de processos não configurada');
      }

      const response = await this.directHttpRepository.get<unknown>(getHabitaProcessFilesUrl(processId));
      return normalizeProcessFiles(response.data, processId);
    } catch (error) {
      console.error('Erro inesperado ao buscar arquivos do processo:', error);
      throw error;
    }
  }

  /**
   * Verifica dados do cliente enviando o objeto customer completo
   */
  async verifyCustomerData(processId: string, customerData: any): Promise<any> {
    try {
      if (!this.processApiUrl) {
        throw new Error('URL da API alternativa não configurada');
      }

      const response = await this.processHttpRepository.post<any>(`/verify/${processId}`, customerData);

      return response.data;
    } catch (error) {
      console.error('Erro inesperado ao verificar dados do cliente:', error);
      throw error;
    }
  }

  /**
   * Busca um processo por ID com informações do cliente
   */
  async findByIdWithCustomer(id: string): Promise<HabitaProcessDetail | null> {
    try {
      if (!this.processApiUrl) {
        throw new Error('URL da API de processos não configurada');
      }

      const response = await this.directHttpRepository.get<unknown>(getHabitaProcessDetailUrl(id));
      return normalizeProcessDetail(response.data, id) as HabitaProcessDetail | null;
    } catch (error) {
      console.error('Erro inesperado ao buscar processo por ID com cliente:', error);
      if (isNotFoundError(error)) return null;
      throw friendlyRepositoryError('Não foi possível carregar os dados do processo agora. Tente novamente em instantes.', error);
    }
  }

  async downloadProcessFile(file: ProcessFileModel): Promise<ProcessFileDownloadResult> {
    const requests = this.buildFileDownloadRequests(file);
    const failures: FileDownloadFailure[] = [];

    for (const request of requests) {
      try {
        return await this.downloadRequest(request);
      } catch (error) {
        failures.push({ error, url: request.url });
      }
    }

    throw buildDownloadError(failures);
  }
}
